import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';

function HiddenUsers() {
  const [hiddenUsers, setHiddenUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add form
  const [emailsInput, setEmailsInput] = useState('');
  const [reason, setReason] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addResult, setAddResult] = useState(null);

  const fetchHiddenUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getHiddenUsers();
      setHiddenUsers(res.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load hidden users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHiddenUsers(); }, [fetchHiddenUsers]);

  const handleHideUsers = async (e) => {
    e.preventDefault();
    if (!emailsInput.trim()) return;

    const emails = emailsInput
      .split(/[,\n]+/)
      .map(e => e.trim())
      .filter(Boolean);

    if (emails.length === 0) return;

    setAddLoading(true);
    setAddResult(null);
    try {
      const res = await adminAPI.hideUsersFromDiscovery({ emails, reason: reason || undefined });
      setAddResult(res.data.results);
      setEmailsInput('');
      setReason('');
      fetchHiddenUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to hide users');
    } finally {
      setAddLoading(false);
    }
  };

  const handleUnhide = async (email) => {
    if (!window.confirm(`Unhide ${email}? They will appear in Nearby/Discover again.`)) return;
    try {
      await adminAPI.unhideUsersFromDiscovery({ emails: [email] });
      fetchHiddenUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to unhide user');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-900">Hidden Users</h1>
        <p className="text-gray-600 mt-1 text-sm">
          Users hidden from Nearby, Discover, and Looking For lists. They can still use the app — others just can't find them.
        </p>
      </div>

      {/* Add Users Form */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border">
        <h2 className="text-lg font-bold mb-4">Hide Users from Discovery</h2>
        <form onSubmit={handleHideUsers} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">
              Email addresses (one per line, or comma-separated)
            </label>
            <textarea
              value={emailsInput}
              onChange={e => setEmailsInput(e.target.value)}
              rows={4}
              placeholder="user1@gmail.com&#10;user2@gmail.com&#10;user3@gmail.com"
              className="w-full border-2 border-gray-200 rounded-xl p-3 text-sm focus:border-pink-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Reason (optional)</label>
            <input
              type="text"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Test accounts, Fake profiles, Internal team"
              className="w-full border-2 border-gray-200 rounded-xl p-3 text-sm focus:border-pink-500 outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={addLoading || !emailsInput.trim()}
            className="px-6 py-3 bg-pink-500 text-white font-semibold rounded-xl hover:bg-pink-600 disabled:opacity-50"
          >
            {addLoading ? 'Processing...' : 'Hide from Discovery'}
          </button>
        </form>

        {/* Result feedback */}
        {addResult && (
          <div className="mt-4 p-4 bg-gray-50 rounded-xl text-sm space-y-1">
            <strong>Results:</strong>
            {addResult.map((r, i) => (
              <div key={i} className={`flex items-center gap-2 ${r.status === 'hidden' ? 'text-green-700' : 'text-red-600'}`}>
                <span>{r.status === 'hidden' ? '✅' : '❌'}</span>
                <span>{r.email} — {r.status === 'hidden' ? `Hidden (${r.profileName})` : 'Not found'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Currently Hidden Users Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border">
        <div className="p-4 border-b bg-gray-50">
          <h2 className="text-lg font-bold">
            Currently Hidden ({hiddenUsers.length})
          </h2>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : hiddenUsers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No users are currently hidden from discovery.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">User</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Reason</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Hidden Since</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {hiddenUsers.map(user => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="font-semibold text-sm">{user.profileName || 'Unknown'}</span>
                    {user.username && (
                      <span className="text-xs text-gray-400 ml-1">@{user.username}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{user.hiddenFromDiscoveryReason || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {user.hiddenFromDiscoveryAt ? new Date(user.hiddenFromDiscoveryAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => handleUnhide(user.email)}
                      className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-200"
                    >
                      Unhide
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default HiddenUsers;
